import { useState, useEffect, useContext } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { ActionButton } from '@/components/ui/action-button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AuthContext } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { inviteUser } from '@/lib/api';
import type { User } from '@/types/auth';

interface FormData {
  name: string;
  email: string;
  role: User['role'];
}

export default function Users() {
  const { session } = useContext(AuthContext);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    role: 'app.viewer',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_users')
        .select('*')
        .order('name');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (session?.user?.role !== 'app.manager') {
      toast.error('You need manager permissions to manage users');
      return;
    }

    setIsSubmitting(true);
    try {
      if (selectedUser) {
        // Update existing user
        const { error } = await supabase
          .from('sms_users')
          .update({
            name: formData.name,
            role: formData.role,
          })
          .eq('id', selectedUser.id);

        if (error) throw error;
        toast.success('User updated successfully');
      } else {
        // Invite new user
        await inviteUser({
          email: formData.email,
          name: formData.name,
          role: formData.role
        });
        toast.success('User invited successfully');
      }

      setModalOpen(false);
      fetchUsers();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to save user';
      console.error('Error saving user:', { error, message: errorMessage });
      toast.error(errorMessage, {
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      const { error } = await supabase
        .from('sms_users')
        .delete()
        .eq('id', selectedUser.id);

      if (error) throw error;

      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const columns = [
    { header: 'Name', accessorKey: 'name' },
    { header: 'Email', accessorKey: 'email' },
    { 
      header: 'Role',
      accessorKey: 'role',
      cell: ({ row }: any) => (
        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
          row.original.role === 'app.manager' 
            ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10'
            : 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'
        }`}>
          {row.original.role === 'app.manager' ? 'Manager' : 'Viewer'}
        </span>
      ),
    },
    {
      header: 'Last Sign In',
      accessorKey: 'last_sign_in_at',
      cell: ({ row }: any) => row.original.last_sign_in_at 
        ? new Date(row.original.last_sign_in_at).toLocaleDateString()
        : 'Never',
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <ActionButton
            icon={Pencil}
            variant="edit"
            onClick={() => {
              setSelectedUser(row.original);
              setFormData({
                name: row.original.name,
                email: row.original.email,
                role: row.original.role,
              });
              setModalOpen(true);
            }}
          />
          <ActionButton
            icon={Trash2}
            variant="delete"
            onClick={() => {
              setSelectedUser(row.original);
              setDeleteDialogOpen(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="text-muted-foreground mt-1">
            Manage system users and their permissions
          </p>
        </div>
        <div className="flex-shrink-0">
          <Button
            onClick={() => {
              setSelectedUser(null);
              setFormData({
                name: '',
                email: '',
                role: 'app.viewer',
              });
              setModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Invite User</span>
            <span className="sm:hidden">Invite</span>
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
      <DataTable
        columns={columns}
        data={users}
        isLoading={isLoading}
      />
      </div>

      <Modal
        title={selectedUser ? 'Edit User' : 'Invite User'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleSubmit}
        confirmText={selectedUser ? 'Save Changes' : 'Send Invitation'}
        confirmDisabled={isSubmitting}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter user's name"
            />
          </div>
          
          {!selectedUser && (
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter user's email"
              />
            </div>
          )}

          <div>
            <label className="text-sm font-medium mb-1 block">Role</label>
            <Select
              value={formData.role}
              onValueChange={(value: User['role']) => setFormData({ ...formData, role: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="app.viewer">Viewer</SelectItem>
                <SelectItem value="app.manager">Manager</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Modal>

      <Modal
        title="Delete User"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        confirmText="Delete"
        confirmVariant="destructive"
      >
        <p>Are you sure you want to delete this user? This action cannot be undone.</p>
      </Modal>
    </div>
  );
}